/**
 * DBE Keeper 패턴 — IndexedDB 디바이스 키 저장소 (Issue #1291).
 *
 * 설계: backend/docs/design/dbe_keeper_pattern_migration.md §4.5
 *
 * 스키마 (v1):
 *   DB: safebox-passkey-dbe (version 1)
 *   ObjectStore: device-keys
 *     key (string): "{user_id}::{field}" — user_id 스코핑 (다중 계정 격리)
 *     value:
 *       client-ecdh-priv: CryptoKey (extractable=false ECDH P-256 priv)
 *       client-ecdh-pub:  CryptoKey (ECDH P-256 pub)
 *       device-id:        string (서버 발급 UUID v4)
 *
 * 정리 정책 (설계 C4):
 *   - 로그아웃 시 IDB 유지 (Keeper 패턴 일치 + UX) — 재로그인 시 자동 잠금해제 가능
 *   - Settings 토글 OFF / 자동 잠금해제 실패(SAFEBOX_843/841) 시에만 idbClearUser 로
 *     해당 user_id 키만 제거 (타 계정 키는 유지)
 */

const DB_NAME = 'safebox-passkey-dbe'
const DB_VERSION = 1
const STORE_NAME = 'device-keys'

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = (e) => {
      const db = e.target.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }
    req.onsuccess = (e) => resolve(e.target.result)
    req.onerror = (e) => reject(e.target.error)
  })
}

function keyFor(userId, field) {
  return `${userId}::${field}`
}

export async function idbPut(userId, field, value) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).put(value, keyFor(userId, field))
    // 내구성: req.onsuccess(메모리 반영)가 아닌 tx.oncomplete(디스크 커밋 완료)에서 resolve.
    // 후속 read 가 미커밋 상태를 보는 race 방지 (Gemini 리뷰 #2, idbClearUser 와 동일 패턴).
    tx.oncomplete = () => resolve()
    tx.onerror = (e) => reject(e.target.error)
    tx.onabort = (e) => reject(e.target.error)
  })
}

export async function idbGet(userId, field) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const req = tx.objectStore(STORE_NAME).get(keyFor(userId, field))
    req.onsuccess = (e) => resolve(e.target.result)
    req.onerror = (e) => reject(e.target.error)
  })
}

/**
 * 특정 user_id 의 모든 키 cleanup (priv, pub, device-id).
 * Settings 토글 OFF 또는 자동 잠금해제 실패(SAFEBOX_843/841) catch 시 호출.
 */
export async function idbClearUser(userId) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    store.delete(keyFor(userId, 'client-ecdh-priv'))
    store.delete(keyFor(userId, 'client-ecdh-pub'))
    store.delete(keyFor(userId, 'device-id'))
    tx.oncomplete = () => resolve()
    tx.onerror = (e) => reject(e.target.error)
  })
}

/**
 * 현재 등록 상태 확인 (Settings 토글 표시 / LoginMasterPW 자동 진입 시도 분기).
 */
export async function idbHasDevice(userId) {
  const deviceId = await idbGet(userId, 'device-id')
  return Boolean(deviceId)
}
